import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { legacyRedirect, type LegacyLocationTarget } from '@/lib/route-map';
import { clearActiveTenant, writeActiveTenant } from '@/lib/active-tenant';

/**
 * Old bookmarks (Phase 1 / T1.6A).
 *
 * `/app/b/{id}/…` used to mean a SITE id and now means a BUSINESS id, so the
 * two shapes collide and a blind client redirect would be a cross-tenant
 * navigation waiting to happen. Nothing here guesses: a path that carries an
 * old Location id is translated by the server
 * (`GET /api/resolve-legacy-site/:siteId`), which proves the caller's
 * membership before it will say what the canonical pair is.
 *
 * A foreign, deleted or out-of-scope bookmark gets the same silent answer as a
 * nonexistent one — the visitor simply lands on `/app`, which then resolves
 * normally. It never reports what it failed to find, so a stale URL cannot be
 * used to ask whether some other tenant's Location exists.
 */
export function LegacyRedirect() {
  const location = useLocation();
  const resolution = legacyRedirect(location.pathname, location.search);
  const target = resolution?.kind === 'resolve-location' ? resolution.target : null;
  const [resolved, setResolved] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    resolveLegacyLocation(target)
      .then((to) => {
        if (!cancelled) setResolved(to);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // The path is fixed for this mount; re-running would re-request the same
    // translation and could loop on a dead bookmark.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.legacyLocationId]);

  if (!resolution) return <Navigate to="/app" replace />;
  if (resolution.kind === 'redirect') return <Navigate to={resolution.to} replace />;
  if (failed) return <Navigate to="/app" replace />;
  if (resolved) return <Navigate to={resolved} replace />;
  return null; // translating — no flash of empty content
}

async function resolveLegacyLocation(target: LegacyLocationTarget): Promise<string> {
  const res = await api.get<{ businessId: string; locationId: string }>(
    `/api/resolve-legacy-site/${encodeURIComponent(target.legacyLocationId)}`,
  );
  // The bookmark that got us here is now the acting tenant — but only because
  // the server just proved this caller may act on it.
  writeActiveTenant({ businessId: res.businessId, locationId: res.locationId });
  return target.rebuild(res.businessId, res.locationId) + target.search;
}

/** Exported for the /app migration path, which needs the same proven translation. */
export async function resolveLegacyLocationId(
  legacyLocationId: string,
): Promise<{ businessId: string; locationId: string } | null> {
  try {
    return await api.get<{ businessId: string; locationId: string }>(
      `/api/resolve-legacy-site/${encodeURIComponent(legacyLocationId)}`,
    );
  } catch {
    // Foreign, deleted, out of scope, or simply gone — all the same answer, and
    // all end the migration rather than retrying.
    clearActiveTenant();
    return null;
  }
}
